import Console from './Console';
import { logError } from './Logger';

// 3PP-Reduce (W-24077647) replaced the `local-storage-fallback` package with
// this inline shim, using only native browser APIs. Keeps the same fallback
// ladder the package provided — localStorage -> sessionStorage -> cookie ->
// in-memory — so a storage-blocked or private-mode browser degrades
// gracefully instead of throwing on every access.
const COOKIE_PREFIX = 'lS_';

// Prefixing + scoping clear() to only prefixed cookies avoids colliding with
// or wiping unrelated cookies (e.g. an auth session cookie) on the same page.
// Exported for testing.
export function cookieStorage() {
  if (typeof document === 'undefined' || typeof document.cookie !== 'string') {
    return null;
  }
  const readAll = () => document.cookie.split('; ').filter(Boolean).map((entry) => {
    const eq = entry.indexOf('=');
    return eq === -1 ? [entry, ''] : [entry.substring(0, eq), entry.substring(eq + 1)];
  });
  return {
    getItem: (key) => {
      const found = readAll().find(([name]) => name === COOKIE_PREFIX + key);
      return found ? decodeURIComponent(found[1]) : null;
    },
    setItem: (key, value) => {
      document.cookie = `${COOKIE_PREFIX}${key}=${encodeURIComponent(value)}; path=/`;
    },
    removeItem: (key) => {
      document.cookie = `${COOKIE_PREFIX}${key}=; path=/; max-age=0`;
    },
    clear: () => {
      readAll()
        .filter(([name]) => name.indexOf(COOKIE_PREFIX) === 0)
        .forEach(([name]) => {
          document.cookie = `${name}=; path=/; max-age=0`;
        });
    },
  };
}

function createMemoryStorage() {
  const data = {};
  return {
    getItem: key => (Object.prototype.hasOwnProperty.call(data, key) ? data[key] : null),
    setItem: (key, value) => {
      data[key] = String(value);
    },
    removeItem: (key) => {
      delete data[key];
    },
    clear: () => {
      Object.keys(data).forEach((key) => {
        delete data[key];
      });
    },
  };
}

// Returns the first candidate store that survives a write/remove probe, otherwise
// an in-memory store. Each candidate is a thunk so an unavailable API (which can
// throw merely on property access) is caught here. Exported for testing.
export function selectStorage(candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    try {
      const store = candidates[i]();
      if (store) {
        const probeKey = '__ls_probe__';
        store.setItem(probeKey, probeKey);
        store.removeItem(probeKey);
        return store;
      }
    } catch (ex) {
      // Candidate blocked or unavailable — fall through and try the next one.
    }
  }
  return createMemoryStorage();
}

const storage = selectStorage([
  () => window.localStorage,
  () => window.sessionStorage,
  () => cookieStorage(),
]);

const getItem = (key) => {
  let item = null;
  try {
    item = storage.getItem(key);
  } catch (ex) {
    Console.log('localStorage.getItem()', ex.code, ex.message);
    logError(
      {
        errorCode: ex.code,
        errorMessage: ex.message,
        callingMethod: 'LocalStorage.getItem()',
      },
      true,
    );
  }
  return item;
};

const setItem = (key, value) => {
  try {
    storage.setItem(key, value);
  } catch (ex) {
    Console.log('LocalStorage.setItem()', ex.code, ex.message);
    logError(
      {
        errorCode: ex.code,
        errorMessage: ex.message,
        callingMethod: 'LocalStorage.setItem()',
      },
      true,
    );
  }
};

const removeItem = (key) => {
  try {
    storage.removeItem(key);
  } catch (ex) {
    Console.log('LocalStorage.removeItem()', ex.code, ex.message);
    logError(
      {
        errorCode: ex.code,
        errorMessage: ex.message,
        callingMethod: 'LocalStorage.removeItem()',
      },
      true,
    );
  }
};

const clear = () => {
  try {
    storage.clear();
  } catch (ex) {
    Console.log('LocalStorage.clear()', ex.code, ex.message);
    logError(
      {
        errorCode: ex.code,
        errorMessage: ex.message,
        callingMethod: 'LocalStorage.clear()',
      },
      true,
    );
  }
};
const LocalStorage = {
  getItem,
  setItem,
  removeItem,
  clear,
};

export default LocalStorage;
